import apiService from './ApiService';

const CHUNK_SIZE_BYTES = 4 * 1024 * 1024;

const createUploadId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return [
      hex.slice(0, 4).join(''),
      hex.slice(4, 6).join(''),
      hex.slice(6, 8).join(''),
      hex.slice(8, 10).join(''),
      hex.slice(10, 16).join('')
    ].join('-');
  }

  const random = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${random()}${random()}-${random()}-${random()}-${random()}-${random()}${random()}${random()}`;
};

const AsistentesClasicosService = {
  async uploadFilesInChunks(files) {
    const uploadedFiles = [];

    for (const file of files || []) {
      const uploadId = createUploadId();
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE_BYTES));

      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
        const start = chunkIndex * CHUNK_SIZE_BYTES;
        const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
        const chunkBlob = file.slice(start, end);
        const chunkFile = new File([chunkBlob], file.name, {
          type: file.type || 'application/pdf'
        });

        const formData = new FormData();
        formData.append('uploadId', uploadId);
        formData.append('fileName', file.name);
        formData.append('contentType', file.type || 'application/pdf');
        formData.append('chunkIndex', String(chunkIndex));
        formData.append('totalChunks', String(totalChunks));
        formData.append('totalSize', String(file.size));
        formData.append('chunk', chunkFile);

        const response = await apiService.request('/AsistentesClasicos/uploads/chunk', {
          method: 'POST',
          body: formData,
          headers: {}
        });

        if (!response?.ok) {
          throw new Error(response?.error || `Error al subir el chunk ${chunkIndex + 1} del archivo ${file.name}`);
        }
      }

      uploadedFiles.push({
        uploadId,
        fileName: file.name,
        contentType: file.type || 'application/pdf',
        length: file.size
      });
    }

    return uploadedFiles;
  },

  async crearAsistente(formData) {
    try {
      const response = await apiService.request('/AsistentesClasicos/crear', {
        method: 'POST',
        body: formData,
        headers: {}
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al crear el asistente clásico');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al crear asistente clásico:', error);
      throw error;
    }
  },

  async actualizarAsistente(idOpenAIAssistant, formData) {
    try {
      const response = await apiService.request(`/AsistentesClasicos/${idOpenAIAssistant}`, {
        method: 'PUT',
        body: formData,
        headers: {}
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al actualizar el asistente clásico');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar asistente clásico:', error);
      throw error;
    }
  }
  ,
  async getAsistente(idOpenAIAssistant) {
    try {
      const response = await apiService.request(`/AsistentesClasicos/${idOpenAIAssistant}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener el GPT clásico');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al obtener GPT clásico:', error);
      throw error;
    }
  },
  async getMisAsistentes(options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.idRegional) params.append('idRegional', options.idRegional);
      if (options.soloFinalizados != null) params.append('soloFinalizados', String(Boolean(options.soloFinalizados)));
      const qs = params.toString();
      const response = await apiService.request(`/AsistentesClasicos/mis-asistentes${qs ? `?${qs}` : ''}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener los GPTs clásicos');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al obtener GPTs clásicos:', error);
      throw error;
    }
  },

  async getAsistentesMisMaterias(options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.idRegional) params.append('idRegional', options.idRegional);
      const qs = params.toString();
      const response = await apiService.request(`/AsistentesClasicos/mis-materias${qs ? `?${qs}` : ''}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener los GPTs de tus materias');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al obtener GPTs de materias:', error);
      throw error;
    }
  },

  async updateActivo(idOpenAIAssistant, activo) {
    try {
      const response = await apiService.request(`/AsistentesClasicos/${idOpenAIAssistant}/activo`, {
        method: 'PUT',
        body: JSON.stringify({ activo }),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al actualizar el estado del GPT clásico');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al actualizar estado del GPT clásico:', error);
      throw error;
    }
  },

  async eliminarAsistente(idOpenAIAssistant) {
    try {
      const response = await apiService.request(`/AsistentesClasicos/${idOpenAIAssistant}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(response.error || 'Error al eliminar el GPT clásico');
      }

      return response;
    } catch (error) {
      console.error('❌ Error al eliminar GPT clásico:', error);
      throw error;
    }
  }
};

export default AsistentesClasicosService;
